/**
 * Canonical URLs for the shared record layer.
 *
 * **Records are module-neutral.** A client is not a Sales record or a Market
 * Intelligence record — it's the shared spine every module hangs off, the same
 * way Salesforce objects and Dynamics' Dataverse tables are shared across
 * modules. So a record has exactly ONE url, independent of which module you were
 * in when you opened it, and the active module comes from nav context instead
 * (see `lib/module-context.ts`).
 *
 * That's a change from the previous `/modules/global/*` scheme, where the module
 * was encoded in the record's own path. It had two costs worth remembering,
 * because both are the reason this module exists:
 *
 *  1. **Drilling into a record was an involuntary module switch.** The route's
 *     own `globalShell` flag decided the chrome, so clicking a client from
 *     inside Sales silently swapped the Sales sidebar for the Global one.
 *  2. **The same record would need one url per module** under any scheme that
 *     keeps the module in the path (`/modules/sales/clients/1` vs
 *     `/modules/market-intel/clients/1`), which breaks recents, sharing, and
 *     every stored link.
 *
 * **Always build record links through these helpers, never by hand.** The
 * previous scheme's paths were hardcoded as string literals in ~41 files —
 * including non-obvious ones (Maxwell's `resource-uri.ts` and
 * `notification-href.ts`, the recents widget, `GlobalSearch`) — which made
 * the last re-path a 41-file change. Routing through one module makes the
 * next one a one-file change.
 */

/**
 * Client (customer) detail.
 *
 * The url keeps `/customers` even though every label says "Clients". That's
 * deliberate: the path is identical on `main`, so the PowerUp module shell can
 * be feature-flagged without record urls differing between flag states — no
 * flag-aware path helper, no dual route registration, and the ~30 files that
 * import from this module need no flag awareness at all. Renaming the url to
 * match the label is a cosmetic win that would have cost exactly the
 * coupling this module exists to avoid.
 */
export function clientPath(id: string): string {
  return `/customers/${id}`;
}

/** Client list. See `clientPath` on why the url says `customers`. */
export const CLIENTS_PATH = "/customers";

/** Supplier detail. */
export function supplierPath(id: string): string {
  return `/suppliers/${id}`;
}

/** Supplier list. */
export const SUPPLIERS_PATH = "/suppliers";

/** Accounts list. No detail route exists today. */
export const ACCOUNTS_PATH = "/accounts";

/** Utilities list. No detail route exists today. */
export const UTILITIES_PATH = "/utilities";

/** Deal detail. Deals are Sales-owned rather than part of the shared record
 *  layer, but the helper lives here so every record-ish link has one home. */
export function dealPath(id: string): string {
  return `/deals/${id}`;
}

/** Deal list. */
export const DEALS_PATH = "/deals";
